import React, { useEffect, useMemo, useState } from "react";
import { Box, CardMedia} from "@mui/material";
import { DataGrid, GridColDef, GridActionsCellItemProps, GridActionsCellItem, GridToolbar } from "@mui/x-data-grid";
import { Beacon } from "../../../../types/models/Beacon";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { getBusinessBeaconsInGroup, getBusinessBeaconsInGroupByCategory, getBusinessBeaconsInGroupTextSearch, getLatestBeacons } from "../../../../redux/actions";
import { getCategories } from "../../../../redux/actions/Category";
import { AppConstants } from "../../../../types/Constants";
import { BeaconColumnInfo, BeaconListContexts } from "./BeaconListTypes";
import { fetchError } from "../../../../redux/actions/Common";
import { getDateTimeString, getImageURLOnServerBeaconFace, getImageURLCategoryListIcon } from "../../../../utils/utils";
import { BeaconActiveTableLabel } from "../../components/BeaconActiveTableLabel";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import BeaconCategory from "../../../../components/BeaconCategory";
import { useAuthContext } from "../../../authentication/contexts/AuthContext";
import { useParams } from "react-router-dom";


interface BeaconListProps {
  beaconColumnInfo: BeaconColumnInfo[];
  refreshFlag: boolean;
  setRefreshFlag: (flag: boolean) => void;
  onOpenBeaconDialog: (group_id: string, beacon?: Beacon, edit?: boolean) => void;
  onOpenBeaconDeleteDialog:(beacon?: Beacon) => void;
  onOpenBeaconLink: (beacon?: Beacon) => void;
  context: typeof BeaconListContexts[number];
  isArchivedView: boolean;
  isArchivedGroup?: boolean;
}

const BeaconList: React.FC<BeaconListProps> = ({ beaconColumnInfo, refreshFlag, setRefreshFlag, onOpenBeaconDialog, onOpenBeaconDeleteDialog, onOpenBeaconLink, context, isArchivedView, isArchivedGroup }) => {

  const dispatch = useAppDispatch();
  const auth = useAuthContext();
  const { group_id } = useParams<{ group_id: string }>();

  const businessGroupState = useAppSelector(state => state.businessGroup);
  const beaconState = useAppSelector(state => state.beacon);
  const categoryGroupState = useAppSelector((state) => state.category);

  const [currentPage, setCurrentPage] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (currentPage !== undefined) {
      getBeaconData();

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (businessGroupState.currentBusinessGroup !== undefined) {
      callGettingBeaconsForFirstPage();
      getCategoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessGroupState.currentBusinessGroup]);

  useEffect(() => {
    if (refreshFlag === true) {
      callGettingBeaconsForFirstPage();
      setRefreshFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag]);

  useEffect(() => {
    if (beaconState.searchText !== null) {
      callGettingBeaconsForFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconState.searchText]);

  useEffect(() => {
    if (beaconState.filterCategory !== null) {
      callGettingBeaconsForFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconState.filterCategory]);

  const getBeaconData = () => {
    switch (context) {
      case "BEACONS":
        getBusinessBeacons();
        break;
      case "MODERATION":
        getLatestOrNearbyBeacons();
        break;
      default:
        getBusinessBeacons();
        break;
    }
  };

  const getCategoryData = () => {
    //Set the categories into state
    if(businessGroupState.currentBusinessGroup){
      const group_id = businessGroupState.currentBusinessGroup.id;
      dispatch(
        getCategories(group_id,(categories) => {
          if (!categories){
            dispatch(fetchError("Failed to retrieve categories"));
          }
      }));
    }
  };

  const callGettingBeaconsForFirstPage = () => {
    if (currentPage === 0) {
      getBeaconData();
    }
    else {
      setCurrentPage(0);
    }
  };

  const getBusinessBeacons = () => {
    setIsLoading(true);
    if (beaconState.searchText && beaconState.searchText.length > 0) {
      dispatch(getBusinessBeaconsInGroupTextSearch(
        businessGroupState.currentBusinessGroup?.id ?? null,
        beaconState.searchText,
        currentPage ?? 0,
        () => { setIsLoading(false); })
      );
    }
    else if (beaconState.filterCategory && beaconState.filterCategory !== AppConstants.AllCategoriesValue) {
      dispatch(getBusinessBeaconsInGroupByCategory(
        businessGroupState.currentBusinessGroup?.id ?? null,
        beaconState.filterCategory,
        currentPage ?? 0,
        () => { setIsLoading(false); })
      );
    }
    else {
      dispatch(getBusinessBeaconsInGroup(
        businessGroupState.currentBusinessGroup?.id ?? null, isArchivedView,
        currentPage ?? 0,
        () => { setIsLoading(false); })
      );
    }
  };

  const getLatestOrNearbyBeacons = () => {
    // TODO: Little bit undercooked, doesnt fully interact with other parts of the component.
    setIsLoading(true);
    dispatch(
      getLatestBeacons(currentPage ?? 0, () => {
        setIsLoading(false);
      })
    );
  };

  const getCategory = (categoryName: string) => {
    if (categoryGroupState.categories) {
      const selectedCategory = categoryGroupState.categories.find(category => category.Name === categoryName);
      if (selectedCategory) {
        return selectedCategory;
      }
    }
  };

  const handleRowClick = (params: any) => {
    onOpenBeaconDialog(group_id ?? "", params.row, true);
  };

  function EditActionItem({
    beacon,
    ...props
  }: GridActionsCellItemProps & { beacon: Beacon }) {
    return (
      <React.Fragment>
        <GridActionsCellItem {...props} onClick={() => onOpenBeaconDialog(group_id ?? "", beacon, true)}/>
      </React.Fragment>
    );
  }

  function DeleteActionItem({
    beacon,
    ...props
  }: GridActionsCellItemProps & { beacon: Beacon }) {
    return (
      <React.Fragment>
        <GridActionsCellItem {...props} onClick={() => onOpenBeaconDeleteDialog(beacon)}/>
      </React.Fragment>
    );
  }

  function LinkActionItem({
    beacon,
    ...props
  }: GridActionsCellItemProps & { beacon: Beacon }) {
    return (
      <React.Fragment>
        <GridActionsCellItem {...props} onClick={() => onOpenBeaconLink(beacon)}/>
      </React.Fragment>
    );
  }

  const columns = useMemo<GridColDef<Beacon>[]>(
    () => [
    {
      field: 'display_name', 
      headerName: 'Title', 
      width: 300,
      flex: 1,
      onClick: (params: any) => (onOpenBeaconDialog(group_id ?? "", params.row, true)),
      renderCell: (params: any) => {

        const category = getCategory(params.row.category);

        return (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center", marginRight: "16px" }}>
          {params.row.has_custom_beacon_face ? (
              <CardMedia
              sx={{ width: "50px", aspectRatio: 1, objectFit: "cover", borderRadius: "50%" }}
              component="img"
              src={getImageURLOnServerBeaconFace(params.row.id, params.row.last_modified)}
              alt="beacon"
            />
          ) : category?.HasBeaconHeadIcon ? (
            <CardMedia
              sx={{ width: "50px", aspectRatio: 1, objectFit: "cover", borderRadius: "50%" }}
              component="img"
              src={getImageURLCategoryListIcon(category.Id ?? "", category.LastModified)}
              alt="beacon icon"
            />
          ) : (
            <ImageOutlinedIcon
              sx={{
                width: "38px",
                height: "38px",
                marginRight: "6px",
                marginLeft: "6px",
                objectFit: "cover",
                borderRadius: "50%",
                color: "grey",
              }}
            />
          )}
          </Box>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis"}}>
          {params.row.display_name}
          </span>
        </Box>
      );
    }
    },
    {field: 'active', headerName: 'Status', width: 150, flex: 0, renderCell: (params: any) => { 
      const isActive = params.row.archived || isArchivedGroup ? false : params.row.active;
      return (
          <BeaconActiveTableLabel active={isActive} />
      );
    }
    },
    {field: 'start_time', headerName: 'Start Time', width: 150, flex: 1, renderCell: (params: any) => (
      <Box>
      {getDateTimeString(params.row.start_time)}
      </Box>
      )},
    {field: 'end_time', headerName: 'End Time', width: 150, flex: 1, renderCell: (params: any) => (
      <Box>
      {getDateTimeString(params.row.end_time)}
      </Box>
      )},
    {field: 'category', headerName: 'Category', minWidth: 220, flex: 1, renderCell: (params: any) => {
      const category = getCategory(params.row.category);
      return (
      <Box sx={{ display: "inline-block", height: "100%", lineHeight: 2, alignContent: "center" }}>
        <BeaconCategory 
        category={category?.Name} 
        justifyContent="left"
        icons={true}
        id={category?.Id}
        borderRadius="22px"
        lastModfied={category?.LastModified}
      />
      </Box>
      );
    }},
    {field: 'views', headerName: 'Views', width: 70, flex: 0},
    {field: 'visits', headerName: 'Visits', width: 70, flex: 0},
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 70,
      flex: 0,
      getActions: (params) => {
        const access_level = businessGroupState.currentBusinessGroup?.access_level;

        if ((!auth.isBusinessUser() && access_level == "owner") || access_level == "read") return [];

        const actions = [
          <EditActionItem
            key="edit"
            label="Edit"
            icon={<EditIcon />}
            showInMenu
            beacon={params.row}
          />,
          <LinkActionItem
            key="link"
            label="Link"
            icon={<LinkIcon />}
            showInMenu
            beacon={params.row}
          />
        ];
        if (access_level == "admin" || access_level == "readwrite" || access_level == "owner") {
        
            actions.push(
            <DeleteActionItem
              key="delete"
              label="Delete"
              icon={<DeleteIcon />}
              showInMenu
              beacon={params.row}
            />
            );
          }
        
        return actions;
      }
    }
    ],
  [businessGroupState.currentBusinessGroup, beaconState.currentBeacons, categoryGroupState, context],
  );

  return (
      <Box sx={{ width: '100%', maxHeight: "calc(100vh - 300px)", overflowY: "auto"}}>
        <DataGrid
          columns={columns}
          rows={beaconState.currentBeacons}
          getRowId={row=>row.id}
          rowHeight={70}
          onRowClick={handleRowClick}
          loading={isLoading}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
        />
      </Box>
  );
};

export default BeaconList;
